/**
 * Contact-directory API service (ADR-141 §4).
 *
 * Backed by the v2 endpoints:
 *   GET/POST            /api/v2/contacts/
 *   PATCH               /api/v2/contacts/{id}/
 *   POST                /api/v2/contacts/{id}/destinations/
 *   PATCH/DELETE        /api/v2/contacts/{id}/destinations/{destination_id}/
 *   POST                /api/v2/contacts/{id}/tags/{tag_id}/
 *   DELETE              /api/v2/contacts/{id}/tags/{tag_id}/remove/
 *   GET/POST            /api/v2/contact-tags/
 *   PATCH/DELETE        /api/v2/contact-tags/{id}/
 *
 * CSV-import lane and directory sources:
 *   POST                /api/v2/contacts/import/            (multipart; dry_run discriminates 200/202)
 *   GET                 /api/v2/contacts/import/{run_id}/   (poll a commit)
 *   GET                 /api/v2/contacts/import/template/   (starter CSV)
 *   GET/POST            /api/v2/contact-directory-sources/
 *   PATCH/DELETE        /api/v2/contact-directory-sources/{id}/
 *   POST                /api/v2/contact-directory-sources/{id}/sync-now/
 *   GET                 /api/v2/contact-directory-sources/{id}/sync-log/
 *
 * Uses the typed `@tiaki/http-client` surface (ADR-113): resource shapes
 * and call sites come straight off `components['schemas']` — no `client.ky`,
 * no hand-written shapes. Both list endpoints are unpaginated (bare array).
 *
 * The server supports `?tag=&source=&active=&query=` filters on the
 * contacts list, but the generated schema does not declare them yet
 * (hand-filtered `get_queryset`, not django-filter — spectacular can't see
 * them). Directory cardinality is bounded (ADR-141 §1: 10²–10⁴ per org),
 * so the SMS surface fetches once and filters client-side; revisit when
 * the schema regen catches up or an org outgrows the bound.
 */

import type { components } from '@tiaki/http-client/types';
import { client } from '../lib/http-client';

export type Contact = components['schemas']['Contact'];
export type ContactDestination = components['schemas']['ContactDestination'];
export type ContactTagAssignment =
  components['schemas']['ContactTagAssignment'];
export type ContactTag = components['schemas']['ContactTag'];
export type ContactSource =
  components['schemas']['ContactProvenanceSourceEnum'];
export type ContactWritePayload = components['schemas']['ContactRequest'];
export type ContactPatchPayload =
  components['schemas']['PatchedContactRequest'];
export type ContactDestinationWritePayload =
  components['schemas']['ContactDestinationRequest'];
export type ContactDestinationPatchPayload =
  components['schemas']['PatchedContactDestinationRequest'];
export type ContactTagWritePayload = components['schemas']['ContactTagRequest'];
export type ContactTagPatchPayload =
  components['schemas']['PatchedContactTagRequest'];
export type ContactImportRun = components['schemas']['ContactImportRun'];
export type ContactImportRunStatus =
  components['schemas']['ContactImportRunStatusEnum'];
export type ContactImportDryRunResponse =
  components['schemas']['ContactImportDryRunResponse'];
export type ContactImportRowReport =
  components['schemas']['ContactImportRowReport'];
export type ContactImportSummary =
  components['schemas']['ContactImportSummary'];
export type ContactDirectorySource =
  components['schemas']['ContactDirectorySource'];
export type ContactDirectorySourceStatus =
  components['schemas']['ContactDirectorySourceStatusEnum'];
export type ContactDirectorySourceWritePayload =
  components['schemas']['ContactDirectorySourceRequest'];
export type ContactDirectorySourcePatchPayload =
  components['schemas']['PatchedContactDirectorySourceRequest'];
export type ContactSyncLog = components['schemas']['ContactSyncLog'];

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

// =============================================================================
// Channel catalog
// =============================================================================

export type ChannelAddressKind =
  | 'email'
  | 'phone'
  | 'webhook_url'
  | 'handle'
  | 'internal';

export interface ContactChannelOption {
  code: string;
  label: string;
  addressKind: ChannelAddressKind;
}

/**
 * The seeded `ContactChannelType` catalog rows (ADR-141 §1; backend
 * `contacts/migrations/0002_seed_catalogs.py`).
 *
 * TODO(ASM-358): there is no channel-types list endpoint yet — the catalog
 * is admin-managed server-side but only exposed embedded in destination
 * rows. This constant mirrors the seed data; replace with a fetch when a
 * `/api/v2/contact-channel-types/` endpoint ships (tracked in the ADR-141
 * tracker notes).
 */
export const CONTACT_CHANNEL_TYPES: readonly ContactChannelOption[] = [
  { code: 'app', label: 'App push', addressKind: 'internal' },
  { code: 'mail', label: 'Email', addressKind: 'email' },
  { code: 'rcs', label: 'Text (RCS/SMS)', addressKind: 'phone' },
  { code: 'voice', label: 'Voice call', addressKind: 'phone' },
  { code: 'slack', label: 'Slack', addressKind: 'webhook_url' },
  { code: 'discord', label: 'Discord', addressKind: 'webhook_url' },
  { code: 'google_chat', label: 'Google Chat', addressKind: 'webhook_url' },
  { code: 'teams', label: 'Microsoft Teams', addressKind: 'webhook_url' },
];

/**
 * Channels an admin can add from the contact drawer. `app` (internal
 * address kind) is excluded: app destinations are valid only on
 * user-linked contacts and are created by the self-service surface
 * (ADR-141 §5), never by an admin form.
 */
export const ADMIN_SELECTABLE_CHANNEL_TYPES: readonly ContactChannelOption[] =
  CONTACT_CHANNEL_TYPES.filter((c) => c.addressKind !== 'internal');

export function channelOption(code: string): ContactChannelOption | undefined {
  return CONTACT_CHANNEL_TYPES.find((c) => c.code === code);
}

export function channelLabel(code: string): string {
  return channelOption(code)?.label ?? code;
}

// =============================================================================
// Client-side address validation hints
// =============================================================================

// Deliberately looser than the server's normalization (phonenumbers /
// EmailValidator / HTTPS check in `ContactDestination.clean()`): these are
// pre-flight hints, the server remains the authority.
const E164_RE = /^\+[1-9]\d{6,14}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function addressPlaceholder(code: string): string {
  switch (channelOption(code)?.addressKind) {
    case 'email':
      return 'name@example.org';
    case 'phone':
      return '+15551234567';
    case 'webhook_url':
      return 'https://hooks.example.com/…';
    case 'handle':
      return '@handle';
    default:
      return '';
  }
}

/**
 * Returns a human-readable error for an invalid destination address, or
 * `null` when the address passes the client-side check for its channel's
 * address kind.
 */
export function validateDestinationAddress(
  code: string,
  address: string,
): string | null {
  const kind = channelOption(code)?.addressKind;
  const trimmed = address.trim();
  switch (kind) {
    case 'internal':
      return null; // no address — resolved via the linked user
    case 'email':
      return EMAIL_RE.test(trimmed) ? null : 'Enter a valid email address';
    case 'phone':
      return E164_RE.test(trimmed)
        ? null
        : 'Use E.164 format, e.g. +15551234567';
    case 'webhook_url': {
      if (!/^https:\/\/.+/.test(trimmed)) {
        return 'Must be an HTTPS URL';
      }
      try {
        new URL(trimmed);
        return null;
      } catch {
        return 'Must be a valid HTTPS URL';
      }
    }
    case 'handle':
      return trimmed ? null : 'Handle is required';
    default:
      return trimmed ? null : 'Address is required';
  }
}

/**
 * Compact per-contact destination summary for the list view, e.g.
 * `"mail ×2, voice ×1"`. Ordered by catalog order; unknown codes last.
 */
export function summarizeDestinations(
  destinations: readonly ContactDestination[],
): string {
  if (destinations.length === 0) {
    return '—';
  }
  const counts = new Map<string, number>();
  for (const destination of destinations) {
    counts.set(
      destination.channel_code,
      (counts.get(destination.channel_code) ?? 0) + 1,
    );
  }
  const catalogOrder = CONTACT_CHANNEL_TYPES.map((c) => c.code);
  return [...counts.entries()]
    .sort(([a], [b]) => {
      const ai = catalogOrder.indexOf(a);
      const bi = catalogOrder.indexOf(b);
      return (ai === -1 ? catalogOrder.length : ai) -
        (bi === -1 ? catalogOrder.length : bi);
    })
    .map(([code, count]) => `${code} ×${count}`)
    .join(', ');
}

// =============================================================================
// Contacts
// =============================================================================

export async function getContacts(): Promise<Contact[]> {
  const result = await client.GET('/api/v2/contacts/');
  return unwrap(result);
}

export async function createContact(
  body: ContactWritePayload,
): Promise<Contact> {
  const result = await client.POST('/api/v2/contacts/', { body });
  return unwrap(result);
}

export async function patchContact(
  id: number,
  body: ContactPatchPayload,
): Promise<Contact> {
  const result = await client.PATCH('/api/v2/contacts/{id}/', {
    params: { path: { id } },
    body,
  });
  return unwrap(result);
}

// =============================================================================
// Contact destinations
// =============================================================================

export async function addContactDestination(
  contactId: number,
  body: ContactDestinationWritePayload,
): Promise<ContactDestination> {
  const result = await client.POST('/api/v2/contacts/{id}/destinations/', {
    params: { path: { id: contactId } },
    body,
  });
  return unwrap(result);
}

export async function patchContactDestination(
  contactId: number,
  destinationId: number,
  body: ContactDestinationPatchPayload,
): Promise<ContactDestination> {
  const result = await client.PATCH(
    '/api/v2/contacts/{id}/destinations/{destination_id}/',
    {
      params: { path: { id: contactId, destination_id: destinationId } },
      body,
    },
  );
  return unwrap(result);
}

export async function deleteContactDestination(
  contactId: number,
  destinationId: number,
): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/contacts/{id}/destinations/{destination_id}/',
    { params: { path: { id: contactId, destination_id: destinationId } } },
  );
  unwrap(result);
}

// =============================================================================
// Contact ↔ tag assignments
// =============================================================================

export async function assignContactTag(
  contactId: number,
  tagId: number,
): Promise<Contact> {
  const result = await client.POST('/api/v2/contacts/{id}/tags/{tag_id}/', {
    params: { path: { id: contactId, tag_id: tagId } },
  });
  return unwrap(result);
}

export async function unassignContactTag(
  contactId: number,
  tagId: number,
): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/contacts/{id}/tags/{tag_id}/remove/',
    { params: { path: { id: contactId, tag_id: tagId } } },
  );
  unwrap(result);
}

// =============================================================================
// Contact tags
// =============================================================================

export async function getContactTags(): Promise<ContactTag[]> {
  const result = await client.GET('/api/v2/contact-tags/');
  return unwrap(result);
}

export async function createContactTag(
  body: ContactTagWritePayload,
): Promise<ContactTag> {
  const result = await client.POST('/api/v2/contact-tags/', { body });
  return unwrap(result);
}

export async function patchContactTag(
  id: number,
  body: ContactTagPatchPayload,
): Promise<ContactTag> {
  const result = await client.PATCH('/api/v2/contact-tags/{id}/', {
    params: { path: { id } },
    body,
  });
  return unwrap(result);
}

export async function deleteContactTag(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/contact-tags/{id}/', {
    params: { path: { id } },
  });
  unwrap(result);
}

// =============================================================================
// CSV import
// =============================================================================

/** Fields the import endpoint accepts in `column_mapping`. */
export const IMPORT_FIELDS = [
  'display_name',
  'email',
  'phone',
  'tags',
] as const;
export type ImportField = (typeof IMPORT_FIELDS)[number];

/** `{field: CSV column header}` — omitted/empty fields are not imported. */
export type ImportColumnMapping = Partial<Record<ImportField, string>>;

export const IMPORT_FIELD_LABELS: Record<ImportField, string> = {
  display_name: 'Name',
  email: 'Email',
  phone: 'Phone',
  tags: 'Tags',
};

/**
 * Parse the header row of a CSV. Minimal RFC-4180 quoting support
 * (quoted fields, doubled quotes) — enough to surface Excel-exported
 * headers in the mapping step. Strips a UTF-8 BOM (the server does the
 * same via utf-8-sig).
 */
export function parseCsvHeader(text: string): string[] {
  const firstLine =
    text.replace(/^\uFEFF/, '').split(/\r\n|\r|\n/, 1)[0] ?? '';
  const cells: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < firstLine.length; i += 1) {
    const ch = firstLine[i];
    if (inQuotes) {
      if (ch === '"') {
        if (firstLine[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      cells.push(cell.trim());
      cell = '';
    } else {
      cell += ch;
    }
  }
  cells.push(cell.trim());
  return cells.filter((c) => c.length > 0);
}

/**
 * Best-effort auto-mapping of common header spellings so the mapping
 * step usually starts pre-filled. Case-insensitive; first match wins.
 */
export function guessColumnMapping(headers: string[]): ImportColumnMapping {
  const candidates: Record<ImportField, RegExp> = {
    display_name: /^(display[ _-]?name|full[ _-]?name|name)$/i,
    email: /^(e-?mail([ _-]?address)?)$/i,
    phone: /^(phone([ _-]?number)?|cell([ _-]?phone)?|mobile)$/i,
    tags: /^(tags?|groups?)$/i,
  };
  const mapping: ImportColumnMapping = {};
  for (const field of IMPORT_FIELDS) {
    const match = headers.find((h) => candidates[field].test(h.trim()));
    if (match !== undefined) {
      mapping[field] = match;
    }
  }
  return mapping;
}

/**
 * Client-side pre-flight for the mapping step (server remains the
 * authority): `display_name` plus at least one of email/phone.
 */
export function validateColumnMapping(
  mapping: ImportColumnMapping,
): string | null {
  if (!mapping.display_name) {
    return 'Map the Name field to a CSV column';
  }
  if (!mapping.email && !mapping.phone) {
    return 'Map at least one of Email or Phone to a CSV column';
  }
  const used = IMPORT_FIELDS.map((f) => mapping[f]).filter(Boolean);
  if (new Set(used).size !== used.length) {
    return 'Each CSV column can only be mapped to one field';
  }
  return null;
}

function importFormData(
  file: File,
  mapping: ImportColumnMapping,
  dryRun: boolean,
): FormData {
  const cleaned: ImportColumnMapping = {};
  for (const field of IMPORT_FIELDS) {
    const column = mapping[field];
    if (column) {
      cleaned[field] = column;
    }
  }
  const formData = new FormData();
  formData.append('file', file);
  formData.append('column_mapping', JSON.stringify(cleaned));
  formData.append('dry_run', dryRun ? 'true' : 'false');
  return formData;
}

/**
 * Multipart POSTs go through `client.ky` (the lib's documented FormData
 * escape hatch — same as `uploadRosterCsv`); the typed `client.POST`
 * path would JSON-serialize the body.
 */
export async function dryRunContactImport(
  file: File,
  mapping: ImportColumnMapping,
): Promise<ContactImportDryRunResponse> {
  const response = await client.ky.post('api/v2/contacts/import/', {
    body: importFormData(file, mapping, true),
  });
  return (await response.json()) as ContactImportDryRunResponse;
}

export async function commitContactImport(
  file: File,
  mapping: ImportColumnMapping,
): Promise<ContactImportRun> {
  const response = await client.ky.post('api/v2/contacts/import/', {
    body: importFormData(file, mapping, false),
  });
  return (await response.json()) as ContactImportRun;
}

export async function getContactImportRun(
  runId: number,
): Promise<ContactImportRun> {
  const result = await client.GET('/api/v2/contacts/import/{run_id}/', {
    params: { path: { run_id: runId } },
  });
  return unwrap(result);
}

/**
 * Starter CSV from the server (headers + one example row). Returns the
 * blob + server-suggested filename; the caller triggers the browser
 * download (`downloadBlob` idiom).
 */
export async function downloadContactImportTemplate(): Promise<{
  blob: Blob;
  filename: string;
}> {
  return client.downloadFile(
    'api/v2/contacts/import/template/',
    {},
    'contact-import-template.csv',
  );
}

/**
 * Best-effort human message out of a failed import POST. The backend
 * answers 400 with `{detail}` or `{column_mapping}`; ky's HTTPError
 * carries the Response. Structural `.response` check rather than
 * `instanceof HTTPError` — ADR 113: no direct ky dependency in the app
 * (same convention as IpawsConfigContent's probeErrorMessage).
 */
export async function importErrorMessage(err: unknown): Promise<string> {
  const response = (err as { response?: Response } | null)?.response;
  if (response && typeof response.clone === 'function') {
    try {
      const body = (await response.clone().json()) as Record<string, unknown>;
      for (const key of ['detail', 'column_mapping'] as const) {
        if (typeof body[key] === 'string' && body[key]) {
          return body[key] as string;
        }
      }
    } catch {
      // Non-JSON error body — fall through to the generic message.
    }
  }
  return err instanceof Error && err.message
    ? err.message
    : 'Import failed. Please try again.';
}

/** Typed view over `ContactImportRun.row_errors` (schema: unknown). */
export interface ImportRowError {
  row: number;
  errors: string[];
}

export function runRowErrors(run: ContactImportRun): ImportRowError[] {
  if (!Array.isArray(run.row_errors)) {
    return [];
  }
  return (run.row_errors as ImportRowError[]).filter(
    (entry) =>
      entry != null &&
      typeof entry.row === 'number' &&
      Array.isArray(entry.errors),
  );
}

// =============================================================================
// Directory sources
// =============================================================================

export interface DirectoryProviderOption {
  key: string;
  label: string;
  /** Registered server-side but not yet configurable from the UI. */
  comingSoon?: boolean;
}

/**
 * Providers registered in the backend registry (`ContactsConfig.ready()`,
 * ADR-141 §8). `ldap` is a tracked adapter slot: listed for
 * parity with the server registry, not yet selectable.
 */
export const DIRECTORY_PROVIDERS: readonly DirectoryProviderOption[] = [
  { key: 'google_workspace', label: 'Google Workspace' },
  { key: 'azure_ad_graph', label: 'Microsoft Entra ID' },
  { key: 'ldap', label: 'LDAP', comingSoon: true },
];

export function directoryProviderLabel(key: string): string {
  return DIRECTORY_PROVIDERS.find((p) => p.key === key)?.label ?? key;
}

/**
 * Typed views over the source's JSON config blobs (schema: unknown).
 * `mapping_config.groups` is the source-group → tag-name map both
 * providers consume; `sync_config` carries the beat-dispatcher knobs.
 */
export interface DirectoryMappingConfig {
  groups?: Record<string, string>;
  phone_channel_codes?: string[];
}

export interface DirectorySyncConfig {
  delta_sync_minutes?: number | null;
  full_sync_hours?: number | null;
}

export function sourceMappingConfig(
  source: ContactDirectorySource,
): DirectoryMappingConfig {
  return (source.mapping_config ?? {}) as DirectoryMappingConfig;
}

export function sourceSyncConfig(
  source: ContactDirectorySource,
): DirectorySyncConfig {
  return (source.sync_config ?? {}) as DirectorySyncConfig;
}

export async function getDirectorySources(): Promise<
  ContactDirectorySource[]
> {
  const result = await client.GET('/api/v2/contact-directory-sources/');
  return unwrap(result);
}

export async function createDirectorySource(
  body: ContactDirectorySourceWritePayload,
): Promise<ContactDirectorySource> {
  const result = await client.POST('/api/v2/contact-directory-sources/', {
    body,
  });
  return unwrap(result);
}

export async function patchDirectorySource(
  id: number,
  body: ContactDirectorySourcePatchPayload,
): Promise<ContactDirectorySource> {
  const result = await client.PATCH(
    '/api/v2/contact-directory-sources/{id}/',
    { params: { path: { id } }, body },
  );
  return unwrap(result);
}

export async function deleteDirectorySource(id: number): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/contact-directory-sources/{id}/',
    { params: { path: { id } } },
  );
  unwrap(result);
}

export async function syncDirectorySourceNow(id: number): Promise<void> {
  const result = await client.POST(
    '/api/v2/contact-directory-sources/{id}/sync-now/',
    { params: { path: { id } } },
  );
  unwrap(result);
}

export async function getDirectorySourceSyncLog(
  id: number,
): Promise<ContactSyncLog[]> {
  const result = await client.GET(
    '/api/v2/contact-directory-sources/{id}/sync-log/',
    { params: { path: { id } } },
  );
  return unwrap(result);
}
